import React, { useMemo } from 'react'
import { Box, Typography, Stack, Button, Chip } from '@mui/material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'

import { blogsData } from '../Data/blogsData'

// لون خلفية السيكشن - نفس لون خلفية HomeLatestProjects اللي فوقه بالظبط
const pageBg = '#2B2B2B'

// ارتفاع ريشة التلاشي العلوية - نفس منطق FADE_HEIGHT في HomeLatestProjects
const TOP_FADE_HEIGHT = { xs: 140, md: 260 }

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut', delay },
  }),
}

// عدد المقالات المعروضة في السيكشن
const BLOGS_LIMIT = 3

export const HomeBlogs = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isArabic = (i18n.language || 'ar').startsWith('ar')
  const lang = isArabic ? 'ar' : 'en'

  const headingFont = isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif"
  const bodyFont = isArabic ? "'IBM Plex Sans Arabic', sans-serif" : "'Manrope', sans-serif"

  // ✨ خد أول 3 مقالات من blogsData
  const posts = useMemo(() => blogsData.slice(0, BLOGS_LIMIT), [])
  const featured = posts[0]
  const rest = posts.slice(1, 3)

  if (!featured) return null

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: pageBg,
        pt: { xs: 10, md: 14 },
        pb: { xs: 10, md: 14 },
        overflow: 'hidden',
      }}
    >
      {/* ريشة التلاشي العلوية - اندماج ناعم مع سيكشن HomeLatestProjects اللي فوقه */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: TOP_FADE_HEIGHT,
          background: `linear-gradient(to bottom,
            ${pageBg} 0%,
            ${pageBg}E6 20%,
            ${pageBg}B3 40%,
            ${pageBg}66 65%,
            ${pageBg}1A 85%,
            transparent 100%)`,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 1300, mx: 'auto', px: { xs: 3, md: 6 } }}>
        {/* الهيدر */}
        <Box
          component={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.6 }}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'flex-end' },
            gap: 3,
            mb: { xs: 6, md: 8 },
          }}
        >
          <Box>
            <Typography
              component={motion.p}
              custom={0}
              variants={fadeUp}
              variant="overline"
              sx={{
                fontFamily: bodyFont,
                color: '#7A2E3A',
                fontWeight: 700,
                letterSpacing: 2,
                display: 'block',
              }}
            >
              {t('blogs.eyebrow')}
            </Typography>
            <Typography
              component={motion.h2}
              custom={0.1}
              variants={fadeUp}
              sx={{
                fontFamily: headingFont,
                fontWeight: isArabic ? 700 : 500,
                fontSize: { xs: '2.2rem', md: '3rem' },
                color: '#D9D6CF',
                mt: 0.5,
                lineHeight: 1.1,
              }}
            >
              {t('blogs.title')}
            </Typography>
          </Box>

          <Box component={motion.div} custom={0.2} variants={fadeUp}>
            <Button
              onClick={() => navigate('/blogs')}
              endIcon={<ArrowForwardIcon sx={{ transform: isArabic ? 'scaleX(-1)' : 'none' }} />}
              variant="contained"
              sx={{
                bgcolor: '#7A2E3A',
                color: '#D9D6CF',
                px: 4,
                py: 1.4,
                borderRadius: '999px',
                fontFamily: bodyFont,
                fontWeight: 600,
                '&:hover': { bgcolor: '#8f3646' },
              }}
            >
              {t('blogs.cta')}
            </Button>
          </Box>
        </Box>

        {/* الشبكة: كارت كبير + كارتين صغيرين */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' },
            gap: { xs: 3, md: 4 },
          }}
        >
          {/* الكارت الكبير المميز */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            onClick={() => navigate(`/blogs/${featured.slug}`)}
            sx={{
              position: 'relative',
              borderRadius: '20px',
              overflow: 'hidden',
              cursor: 'pointer',
              minHeight: { xs: 420, md: 560 },
              border: '1px solid rgba(217,214,207,0.12)',
              boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
            }}
          >
            <Box
              component="img"
              src={featured.image}
              alt={featured.title[lang]}
              loading="lazy"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(43,43,43,0) 30%, rgba(43,43,43,0.95) 100%)',
              }}
            />

            <Stack
              spacing={1.2}
              sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 4.5 } }}
            >
              <Chip
                label={featured.category[lang]}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: 'rgba(122,46,58,0.85)',
                  color: '#D9D6CF',
                  fontFamily: bodyFont,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              <Typography
                sx={{
                  fontFamily: headingFont,
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  color: '#FFFFFF',
                  lineHeight: 1.25,
                }}
              >
                {featured.title[lang]}
              </Typography>
              <Typography
                sx={{
                  fontFamily: bodyFont,
                  color: 'rgba(217,214,207,0.75)',
                  fontSize: '0.95rem',
                  display: { xs: 'none', sm: '-webkit-box' },
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {featured.excerpt[lang]}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ pt: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 14, color: '#D9A5A5' }} />
                <Typography sx={{ fontFamily: bodyFont, color: 'rgba(217,214,207,0.65)', fontSize: '0.8rem' }}>
                  {featured.dateDisplay[lang]}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* الكروت الصغيرة الجانبية */}
          <Stack spacing={{ xs: 3, md: 4 }}>
            {rest.map((post, index) => (
              <Box
                key={post.id}
                component={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 * (index + 1) }}
                onClick={() => navigate(`/blogs/${post.slug}`)}
                sx={{
                  position: 'relative',
                  borderRadius: '18px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  flex: 1,
                  minHeight: { xs: 200, md: 264 },
                  border: '1px solid rgba(217,214,207,0.12)',
                  boxShadow: '0 15px 30px rgba(0,0,0,0.35)',
                }}
              >
                <Box
                  component="img"
                  src={post.image}
                  alt={post.title[lang]}
                  loading="lazy"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(43,43,43,0) 35%, rgba(43,43,43,0.92) 100%)',
                  }}
                />

                <Stack
                  spacing={0.6}
                  sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 2.2, md: 2.8 } }}
                >
                  <Chip
                    label={post.category[lang]}
                    size="small"
                    sx={{
                      alignSelf: 'flex-start',
                      bgcolor: 'rgba(122,46,58,0.85)',
                      color: '#D9D6CF',
                      fontFamily: bodyFont,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: headingFont,
                      fontWeight: 600,
                      fontSize: { xs: '1.05rem', md: '1.15rem' },
                      color: '#FFFFFF',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {post.title[lang]}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.6}>
                    <CalendarTodayIcon sx={{ fontSize: 12, color: '#D9A5A5' }} />
                    <Typography
                      sx={{ fontFamily: bodyFont, color: 'rgba(217,214,207,0.65)', fontSize: '0.75rem' }}
                    >
                      {post.dateDisplay[lang]}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* رتوش الـ gradient اللي بتعمل التداخل مع سيكشن الكونتاكت اللي بعده */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: { xs: 140, md: 200 },
          pointerEvents: 'none',
          background: 'linear-gradient(180deg, rgba(43,43,43,0) 0%, #141312 100%)',
        }}
      />
    </Box>
  )
}